import {FIELD_BIRDS} from '../../configs/fieldAmbience';

/**
 * Стая птиц, пролетающая над полем: клин (как летят гуси и утки) или
 * беспорядочная стайка. Чистые функции без Phaser и DOM — по этому описанию
 * птиц каждый кадр расставляет сцена (см. scenes/FieldAmbience).
 *
 * Стая летит по прямой от одного бокового края пейзажа до другого — над
 * игровым полем или рядом с ним. У каждой птицы свое место в строю (вдоль
 * курса и поперек), свой ритм взмахов и свое небольшое блуждание вокруг места:
 * строй дышит, а не едет картинкой. Вся стая — одного цвета: белые птицы,
 * черные или коричневые.
 *
 * Время в расчет приходит снаружи (сколько стая уже летит): положение птицы —
 * функция времени, без накопления шагов. Пропавший кадр или вкладка, которую
 * скрыли и вернули, стаю не ломают.
 *
 * В состояние партии случайность не попадает: антураж целиком визуальный, у
 * каждого клиента свой (детерминизм записей матчей не задет).
 */

export const BIRD_FLOCK_KINDS = {
  WEDGE: 'wedge',
  SWARM: 'swarm',
};

const TWO_PI = Math.PI * 2;

const randomRange = (rng, min, max) => min + rng() * (max - min);
const randomInt = (rng, min, max) => Math.min(max, Math.floor(randomRange(rng, min, max + 1)));
const toRadians = (degrees) => (degrees * Math.PI) / 180;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** Вид стаи — по весам из конфига */
const pickFlockKind = (rng) => {
  const kinds = Object.values(BIRD_FLOCK_KINDS);
  const totalWeight = kinds.reduce((sum, kind) => sum + FIELD_BIRDS.formations[kind].weight, 0);
  let roll = rng() * totalWeight;
  for (const kind of kinds) {
    roll -= FIELD_BIRDS.formations[kind].weight;
    if (roll < 0) {
      return kind;
    }
  }
  return kinds[kinds.length - 1];
};

/** Птица на своем месте в строю: ритм взмахов и блуждание вокруг места */
const createBird = ({along, across, hexSize, formation, rng}) => ({
  along,
  across,
  sizeFactor: randomRange(rng, formation.sizeFactor.min, formation.sizeFactor.max),
  flapPeriodMs: randomRange(rng, formation.flapPeriodMs.min, formation.flapPeriodMs.max),
  flapPhase: rng(),
  wander: {
    alongAmplitude: hexSize * formation.wanderFactor * randomRange(rng, 0.5, 1),
    acrossAmplitude: hexSize * formation.wanderFactor * randomRange(rng, 0.5, 1),
    alongPeriodMs: randomRange(rng, formation.wanderPeriodMs.min, formation.wanderPeriodMs.max),
    acrossPeriodMs: randomRange(rng, formation.wanderPeriodMs.min, formation.wanderPeriodMs.max),
    alongPhase: rng(),
    acrossPhase: rng(),
  },
});

/**
 * Клин: вожак впереди, остальные — двумя плечами за ним. Плечи редко равны:
 * одно обычно длиннее другого
 */
const buildWedgeBirds = ({hexSize, rng}) => {
  const formation = FIELD_BIRDS.formations[BIRD_FLOCK_KINDS.WEDGE];
  const birdCount = randomInt(rng, formation.birdCount.min, formation.birdCount.max);
  const spacing = hexSize * formation.spacingFactor;
  const armAngle = toRadians(formation.armAngleDeg);
  const jitter = hexSize * formation.jitterFactor;

  const followerCount = birdCount - 1;
  const firstArmCount = Math.round(followerCount * randomRange(rng, formation.armShare.min, formation.armShare.max));
  const birds = [createBird({along: 0, across: 0, hexSize, formation, rng})];
  [[1, firstArmCount], [-1, followerCount - firstArmCount]].forEach(([side, armCount]) => {
    for (let rank = 1; rank <= armCount; rank += 1) {
      birds.push(createBird({
        along: -rank * spacing * Math.cos(armAngle) + randomRange(rng, -jitter, jitter),
        across: side * rank * spacing * Math.sin(armAngle) + randomRange(rng, -jitter, jitter),
        hexSize, formation, rng,
      }));
    }
  });

  return birds;
};

/**
 * Стайка: птицы рассыпаны по пятну, вытянутому вдоль курса, и заметно
 * блуждают — пятно все время меняет очертания
 */
const buildSwarmBirds = ({hexSize, rng}) => {
  const formation = FIELD_BIRDS.formations[BIRD_FLOCK_KINDS.SWARM];
  const birdCount = randomInt(rng, formation.birdCount.min, formation.birdCount.max);
  const radius = hexSize * randomRange(rng, formation.radiusFactor.min, formation.radiusFactor.max);

  const birds = [];
  for (let index = 0; index < birdCount; index += 1) {
    const angle = rng() * TWO_PI;
    // Корень дает равномерную плотность: без него птицы сбиваются к центру
    const distance = radius * Math.sqrt(rng());
    birds.push(createBird({
      along: Math.cos(angle) * distance * formation.stretch,
      across: Math.sin(angle) * distance,
      hexSize, formation, rng,
    }));
  }

  return birds;
};

const BIRD_BUILDERS = {
  [BIRD_FLOCK_KINDS.WEDGE]: buildWedgeBirds,
  [BIRD_FLOCK_KINDS.SWARM]: buildSwarmBirds,
};

/**
 * @param {Object} params
 * @param {{left: number, right: number, top: number, bottom: number}} params.area —
 *   прямоугольник пейзажа, который стая пересекает от бокового края до бокового
 * @param {number} params.hexSize — размер ячейки: от него считаются размеры
 *   строя и скорость
 * @param {Function} [params.rng] — источник случайности (в тестах — заглушка)
 * @param {string} [params.kind] — вид стаи (BIRD_FLOCK_KINDS); не задан — по весам
 * @param {number} [params.direction] — 1: слева направо, -1: справа налево;
 *   не задано — случайно
 * @param {{top: number, bottom: number}} [params.lane] — полоса по вертикали,
 *   в которой проходит курс (игровое поле с запасом); не задана — вся высота
 *   пейзажа
 * @param {number[]} [params.colors] — какого цвета бывают стаи на этой
 *   подложке: стае достается один из них
 * @returns {Object|null} стая; null — лететь негде
 */
export const buildBirdFlock = ({area, hexSize, rng = Math.random, kind, direction, lane, colors} = {}) => {
  if (!area || !(hexSize > 0) || !(area.right > area.left) || !(area.bottom > area.top)) {
    return null;
  }

  const flockKind = BIRD_BUILDERS[kind] ? kind : pickFlockKind(rng);
  const flockDirection = direction === 1 || direction === -1 ? direction : (rng() < 0.5 ? 1 : -1);
  const color = colors?.length > 0 ? colors[Math.min(colors.length - 1, Math.floor(rng() * colors.length))] : null;
  const birds = BIRD_BUILDERS[flockKind]({hexSize, rng});

  // На столько стая стартует за краем и уходит за другой: чтобы ни одна птица
  // не появилась и не пропала на виду
  const extent = birds.reduce((largest, bird) => Math.max(
    largest,
    Math.hypot(bird.along, bird.across) + Math.max(bird.wander.alongAmplitude, bird.wander.acrossAmplitude)
  ), 0) + hexSize * (FIELD_BIRDS.swayFactor + FIELD_BIRDS.wingspanFactor);

  // По темным верхнему и нижнему краям пейзажа курс не идет никогда; полоса
  // поля сужает его еще — стая должна пройти над полем или рядом с ним
  const edgeMargin = (area.bottom - area.top) * FIELD_BIRDS.laneMarginShare;
  const areaTop = area.top + edgeMargin;
  const areaBottom = area.bottom - edgeMargin;
  const laneTop = lane ? clamp(lane.top, areaTop, areaBottom) : areaTop;
  const laneBottom = lane ? clamp(lane.bottom, laneTop, areaBottom) : areaBottom;
  const fromX = flockDirection > 0 ? area.left - extent : area.right + extent;
  const toX = flockDirection > 0 ? area.right + extent : area.left - extent;
  const fromY = randomRange(rng, laneTop, laneBottom);
  const tilt = toRadians(randomRange(rng, -FIELD_BIRDS.headingTiltDeg, FIELD_BIRDS.headingTiltDeg));
  // Курс с наклоном, но за верх и низ пейзажа стая не уходит
  const toY = clamp(fromY + Math.tan(tilt) * Math.abs(toX - fromX), laneTop, laneBottom);

  const speed = hexSize * randomRange(rng, FIELD_BIRDS.speedFactor.min, FIELD_BIRDS.speedFactor.max);
  const length = Math.hypot(toX - fromX, toY - fromY);

  return {
    kind: flockKind,
    direction: flockDirection,
    color,
    from: {x: fromX, y: fromY},
    heading: Math.atan2(toY - fromY, toX - fromX),
    speed,
    durationMs: (length / speed) * 1000,
    // Вся стая слегка гуляет поперек курса — прямая не выглядит линейкой
    sway: {
      amplitude: hexSize * FIELD_BIRDS.swayFactor,
      periodMs: randomRange(rng, FIELD_BIRDS.swayPeriodMs.min, FIELD_BIRDS.swayPeriodMs.max),
      phase: rng(),
    },
    birds,
  };
};

/**
 * Где птица и как она выглядит через elapsedMs после старта стаи.
 *
 * @param {Object} flock — стая из buildBirdFlock
 * @param {Object} bird — птица этой стаи
 * @param {number} elapsedMs
 * @returns {{x: number, y: number, rotation: number, wingSpread: number}}
 *   rotation — куда птица смотрит (радианы, 0 — вправо); wingSpread — размах
 *   крыльев в долях полного (взмах сверху виден как укорочение размаха)
 */
export const resolveBirdPose = (flock, bird, elapsedMs) => {
  const travelled = (flock.speed * elapsedMs) / 1000;
  const cos = Math.cos(flock.heading);
  const sin = Math.sin(flock.heading);

  const swayAngle = TWO_PI * (elapsedMs / flock.sway.periodMs + flock.sway.phase);
  const alongAngle = TWO_PI * (elapsedMs / bird.wander.alongPeriodMs + bird.wander.alongPhase);
  const acrossAngle = TWO_PI * (elapsedMs / bird.wander.acrossPeriodMs + bird.wander.acrossPhase);

  const along = travelled + bird.along + bird.wander.alongAmplitude * Math.sin(alongAngle);
  const across = bird.across
    + flock.sway.amplitude * Math.sin(swayAngle)
    + bird.wander.acrossAmplitude * Math.sin(acrossAngle);

  // Птица смотрит туда, куда летит: скорость блуждания доворачивает ее от курса
  const angularSpeed = (periodMs) => (TWO_PI * 1000) / periodMs;
  const alongSpeed = flock.speed
    + bird.wander.alongAmplitude * angularSpeed(bird.wander.alongPeriodMs) * Math.cos(alongAngle);
  const acrossSpeed = flock.sway.amplitude * angularSpeed(flock.sway.periodMs) * Math.cos(swayAngle)
    + bird.wander.acrossAmplitude * angularSpeed(bird.wander.acrossPeriodMs) * Math.cos(acrossAngle);

  const flapPhase = (elapsedMs / bird.flapPeriodMs + bird.flapPhase) % 1;

  return {
    x: flock.from.x + cos * along - sin * across,
    y: flock.from.y + sin * along + cos * across,
    rotation: flock.heading + Math.atan2(acrossSpeed, alongSpeed),
    wingSpread: 1 - FIELD_BIRDS.flapDepth * (0.5 - 0.5 * Math.cos(TWO_PI * flapPhase)),
  };
};

/** Стая долетела: все птицы ушли за дальний край */
export const checkIfBirdFlockDone = (flock, elapsedMs) => elapsedMs >= flock.durationMs;

/**
 * Через сколько появится следующая стая
 *
 * @param {Function} rng
 * @param {boolean} [isFirst] — первая стая матча: появляется раньше
 * @returns {number} мс
 */
export const resolveBirdFlockDelayMs = (rng = Math.random, isFirst = false) => {
  const range = isFirst ? FIELD_BIRDS.firstFlockDelayMs : FIELD_BIRDS.flockIntervalMs;
  return randomRange(rng, range.min, range.max);
};
